import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class RevenueClient {
  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/revenue`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error.response?.data || error.message);
      }
    );
  }

  // Create revenue account for user
  async createRevenueAccount() {
    try {
      const response = await this.client.post('/create-account');
      return response;
    } catch (error) {
      throw new Error(`Failed to create revenue account: ${error.message}`);
    }
  }

  // Process trade profit
  async processTradeProfit(tradeData) {
    try {
      const response = await this.client.post('/process-trade-profit', tradeData);
      return response;
    } catch (error) {
      throw new Error(`Failed to process trade profit: ${error.message}`);
    }
  }

  // Get revenue summary
  async getRevenueSummary(month = null) {
    try {
      const params = month ? { month } : {};
      const response = await this.client.get('/summary', { params });
      return response;
    } catch (error) {
      throw new Error(`Failed to get revenue summary: ${error.message}`);
    }
  }

  // Setup payment method
  async setupPaymentMethod(paymentData) {
    try {
      const response = await this.client.post('/setup-payment', paymentData);
      return response;
    } catch (error) {
      throw new Error(`Failed to setup payment method: ${error.message}`);
    }
  }

  // Get transactions
  async getTransactions(limit = 20, offset = 0) {
    try {
      const response = await this.client.get('/transactions', {
        params: { limit, offset }
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to get transactions: ${error.message}`);
    }
  }

  // Generate monthly report
  async generateMonthlyReport(month) {
    try {
      const response = await this.client.post('/generate-report', { month });
      return response;
    } catch (error) {
      throw new Error(`Failed to generate monthly report: ${error.message}`);
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response;
    } catch (error) {
      throw new Error(`Revenue service health check failed: ${error.message}`);
    }
  }
}

// Create and export singleton instance
export const revenueClient = new RevenueClient();
export default revenueClient;
